export type ApiAction = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  endpoint: string
  body?: string | FormData
  meta?: any
  headers?: (headers: Record<string, string>) => Record<string, string>
  credentials?: RequestCredentials
  /** Outlives the page that sent it — for a request the user's navigation must not cancel. */
  keepalive?: boolean
  priority?: RequestInit['priority']
}

export type ApiActionDescriptor<L extends string = string> = {
  label: L
}

export type ApiActionCallback<RR, L extends string = string> = {
  (dispatch: <R>(action: ApiAction) => Promise<R>): Promise<RR>
  [API_ACTION_SYMBOL]: ApiActionDescriptor<L>
}

export type ApiDispatcherDispatchOptions = {
  abortSignal?: AbortSignal
  /** Who the request goes out as when not the session in use; neither is refreshed and retried on a 401. */
  session?: ApiSession
}

/** `anonymous` sends as nobody: no Bearer, and the headers that identify the caller left out. `explicit` signs with another session's jwt. */
export type ApiSession = { type: 'anonymous' } | { type: 'explicit'; token: string }

export interface ApiDispatcherInterface {
  dispatch: <RR>(apiAction: (dispatch: <R>(action: ApiAction) => Promise<R>, options?: ApiDispatcherDispatchOptions) => Promise<RR>) => Promise<RR>
}

export const API_ACTION_SYMBOL = Symbol('API_ACTION_SYMBOL')

export const createApiAction = <RR, L extends string>(
  label: L,
  cb: (dispatch: <R>(action: ApiAction) => Promise<R>) => Promise<RR>
): ApiActionCallback<RR, L> => {
  const action = cb as ApiActionCallback<RR, L>
  action[API_ACTION_SYMBOL] = { label }
  return action
}

export const getApiActionDescriptor = (apiAction: ApiActionCallback<any>): ApiActionDescriptor => {
  return apiAction[API_ACTION_SYMBOL]
}

/**
 * The request an action would send, without sending it — for a stub that has to answer *about*
 * what was asked, not merely that it was asked. The id in `/api/v2/places/42` is only knowable
 * this way: the label is shared by every call to that endpoint.
 *
 * The action is run with a dispatch that captures and never resolves, so the body of the action
 * beyond that first dispatch does not execute. Returns `null` for an action that dispatches
 * nothing synchronously, which no api action does today.
 */
export const readApiRequest = (apiAction: ApiActionCallback<any>): ApiAction | null => {
  let captured: ApiAction | null = null
  void apiAction(action => {
    captured = action
    return new Promise<never>(() => {})
  }).catch(() => {})
  return captured
}
